import { useEffect, useState } from "react";
import Spinner from "@/Components/Spinner/Spinner";
import { selectedGarnishAtom } from "@/shared/atoms";
import useCategoryHook from "@/shared/hooks/useCategoryHook";
import { useAtom } from "jotai";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Topping } from "../../shared/utils/interfaces";
import Checkbox from "../Checkbox/Checkbox";
import Icon from "../Icon/Icon";
import IconButton from "../IconButton/IconButton";
import Textfield from "../Input/Textfield";
import TextfieldIntControlled from "../Input/TextfieldIntControlled";
import Select from "../Select/Select";
import useVatHook from "@/shared/hooks/useVatHook";

interface ToppingListProps {
  topping: Partial<Topping>;
  onCreate?: (data: Topping) => void;
  setCreate?: (data: boolean) => void;
  refetch?: () => void;
  hasSelectedGarnish?: boolean;
}

const ToppingList = ({
  topping,
  setCreate,
  refetch,
  hasSelectedGarnish,
}: ToppingListProps) => {
  const { t } = useTranslation();
  const [edit, setEdit] = useState(setCreate ? true : false);
  const methods = useForm();
  const { register, errors, handleSubmit, control, reset } = methods;
  const { toppingUpdate, toppingRemove, toppingCreate } = useCategoryHook();
  const [selectedGarnish, setSelectedGarnish] = useAtom(selectedGarnishAtom);
  const { vats } = useVatHook();

  useEffect(() => {
    reset(topping);
  }, [topping]);

  const onSubmit = (data) => {
    const req: Topping = {
      ...topping,
      ...data,
    };

    if (setCreate) {
      toppingCreate.mutateAsync(req, {
        onSuccess: () => {
          refetch();
          setCreate(false);
        },
      });
    } else {
      toppingUpdate.mutateAsync(req, {
        onSuccess: () => {
          if (hasSelectedGarnish) {
            setSelectedGarnish({
              ...selectedGarnish,
              sub_content: selectedGarnish.sub_content.map((topping) =>
                topping.item_id === req.item_id ? req : topping,
              ),
            });
          }
          refetch();
          setEdit(false);
        },
      });
    }
  };
  const WrapperTag = edit ? "form" : "div";

  return (
    <FormProvider {...methods}>
      <WrapperTag
        onSubmit={edit ? handleSubmit(onSubmit) : undefined}
        className="w-full min-w-0 py-4"
      >
        <div className="flex min-w-0 flex-col gap-4 rounded-xl border border-border p-4">
          <div className="flex items-center justify-between gap-4 border-b border-border pb-3">
            <Controller
              defaultValue={topping.is_active ?? false}
              name="is_active"
              control={control}
              render={(props) => (
                <div className="flex shrink-0 items-center gap-2">
                  <span className="font-semibold">{t("menu.isActive")}</span>
                  <Checkbox
                    active={props.value}
                    onClick={() => props.onChange(!props.value)}
                    disabled={edit === false}
                  />
                </div>
              )}
            />

            {edit ? (
              toppingUpdate.isPending ||
              toppingRemove.isPending ||
              toppingCreate.isPending ? (
                <Spinner></Spinner>
              ) : (
                <div className="flex items-center">
                  <IconButton
                    type="submit"
                    variants="check"
                    variant="success"
                    size="small"
                  />
                  {!setCreate && (
                    <div onClick={() => setEdit(false)} className="mx-12">
                      <IconButton
                        type="button"
                        variants="times"
                        variant="danger"
                        size="small"
                      />
                    </div>
                  )}
                  {!setCreate && (
                    <div className="mr-12">
                      <IconButton
                        onClick={() => {
                          toppingRemove.mutateAsync(topping.item_id, {
                            onSuccess: () => {
                              setEdit(false);
                              refetch();
                            },
                          });
                        }}
                        type="button"
                        variants="trash"
                        variant="danger"
                        size="small"
                      />
                    </div>
                  )}
                </div>
              )
            ) : (
              <div onClick={() => setEdit(true)} className="cursor-pointer">
                <Icon className="h-10 w-10 fill-primary" variants="edit" />
              </div>
            )}
          </div>

          <div className="grid min-w-0 md:grid-cols-2 gap-4">
            <div className="whitespace-nowrap">
              <TextfieldIntControlled
                inputClassName="!bg-white"
                label={t("menu.extraName")}
                placeholder={t("menu.extraNamePlaceholder")}
                required
                name="name"
                defaultValue={topping?.name || {}}
                disabled={edit === false}
              />
            </div>

            <TextfieldIntControlled
              inputClassName="!bg-white"
              label={t("menu.description")}
              placeholder={t("menu.extraDescriptionPlaceholder")}
              name="description"
              defaultValue={topping?.description || {}}
              disabled={edit === false}
            />

            <Textfield
              placeholder={t("price")}
              label={t("price")}
              disabled={!edit}
              inputClassName="!bg-white"
              step="0.01"
              type="number"
              ref={register({
                required: {
                  value: edit,
                  message: t("fieldRequired"),
                },
              })}
              defaultValue={topping.price}
              name="price"
              error={errors?.price?.message}
            />

            <Controller
              defaultValue={topping.vat_enum || vats[1].id}
              name="vat_enum"
              control={control}
              render={(props) => {
                return (
                  <div className="w-full min-w-0">
                    <Select
                      menuPortalTarget={document.getElementById("root")}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({
                          ...base,
                          backgroundColor: "white",
                        }),
                      }}
                      label={t("vat")}
                      onChange={(v) => props.onChange(v.id)}
                      value={vats.find((c) => c?.id === props.value)}
                      isDisabled={edit === false}
                      options={vats}
                      getOptionLabel={(option) => t(option.label)}
                      getOptionValue={(option) => option.id}
                    />
                  </div>
                );
              }}
            />
          </div>
        </div>
      </WrapperTag>
    </FormProvider>
  );
};

export default ToppingList;
